import { MDC_DEGREES, SCORE_WEIGHTS, THRESHOLDS, TIMING } from '@erectus/shared';
import type {
  Episode,
  FrameRecord,
  FrameState,
  MetricStats,
  ScoreWeights,
  SessionSummaryDTO,
} from '@erectus/shared';
import { clamp01, metricStats, round2 } from './stats.js';

/**
 * Session aggregation (docs/ARCHITECTURE.md §1.5).
 *
 * Frames are samples, not durations, so each frame is weighted by the gap to the
 * next one. Gaps are clamped: a pause or a backgrounded tab must not be charged
 * to the user as hours of slouching.
 */

export interface AggregateOptions {
  weights?: ScoreWeights;
  sampleFps?: number;
}

export interface SessionAggregate {
  durationSec: number;
  validRatio: number;
  score: number;
  goodRatio: number;
  fhpMin: number;
  slouchMin: number;
  reclineMin: number;
  recoveryRate: number;
  cva: MetricStats | null;
  cha: MetricStats | null;
  trunk: MetricStats | null;
  prot: MetricStats | null;
  hip: MetricStats | null;
  episodes: Episode[];
  decay: number[];
  timeToFirst: number | null;
  stateSeconds: Record<FrameState, number>;
}

const EMPTY_STATE_SECONDS: Record<FrameState, number> = {
  good: 0,
  forwardHead: 0,
  slouch: 0,
  recline: 0,
  invalid: 0,
};

export function aggregateSession(
  frames: readonly FrameRecord[],
  options: AggregateOptions = {},
): SessionAggregate {
  const weights = options.weights ?? SCORE_WEIGHTS;
  const sampleFps = options.sampleFps ?? 3;
  /** A gap longer than 3 sampling intervals means the recorder was paused. */
  const maxGapMs = (3 / sampleFps) * 1000;

  const stateSeconds: Record<FrameState, number> = { ...EMPTY_STATE_SECONDS };
  const cvaValues: number[] = [];
  const chaValues: number[] = [];
  const trunkValues: number[] = [];
  const protValues: number[] = [];
  const hipValues: number[] = [];

  const bucketMs = TIMING.decayBucketSec * 1000;
  const decayGood: number[] = [];
  const decayValid: number[] = [];

  const episodes: Episode[] = [];
  let runState: FrameState | null = null;
  let runStart = 0;
  let runEnd = 0;

  const closeRun = (endMs: number): void => {
    if (runState === null) return;
    if (runState !== 'good' && runState !== 'invalid') {
      const durationSec = (endMs - runStart) / 1000;
      if (durationSec >= TIMING.episodeMinSec) {
        episodes.push({
          start: round2(runStart / 1000),
          end: round2(endMs / 1000),
          state: runState,
          recovered: false,
        });
      }
    }
    runState = null;
  };

  for (let i = 0; i < frames.length; i += 1) {
    const frame = frames[i]!;
    const next = frames[i + 1];
    const rawGap = next ? next.t - frame.t : 1000 / sampleFps;
    const dtMs = Math.min(Math.max(rawGap, 0), maxGapMs);
    const dtSec = dtMs / 1000;

    stateSeconds[frame.state] += dtSec;

    if (frame.state !== 'invalid') {
      if (frame.cva !== null) cvaValues.push(frame.cva);
      if (frame.cha !== null) chaValues.push(frame.cha);
      if (frame.trunk !== null) trunkValues.push(frame.trunk);
      if (frame.prot !== null) protValues.push(frame.prot);
      if (frame.hip !== null) hipValues.push(frame.hip);
    }

    const bucket = Math.floor(frame.t / bucketMs);
    while (decayGood.length <= bucket) {
      decayGood.push(0);
      decayValid.push(0);
    }
    if (frame.state !== 'invalid') {
      decayValid[bucket] = (decayValid[bucket] ?? 0) + dtSec;
      if (frame.state === 'good') decayGood[bucket] = (decayGood[bucket] ?? 0) + dtSec;
    }

    if (frame.state !== runState) {
      closeRun(frame.t);
      runState = frame.state;
      runStart = frame.t;
    }
    runEnd = frame.t + dtMs;
  }
  closeRun(runEnd);

  // An episode counts as "recovered" when the user is back to good posture within
  // TIMING.recoveryWindowSec of the episode ending.
  for (const episode of episodes) {
    const endMs = episode.end * 1000;
    const limit = endMs + TIMING.recoveryWindowSec * 1000;
    const recovered = frames.some((f) => f.t > endMs && f.t <= limit && f.state === 'good');
    episode.recovered = recovered;
  }

  const validSec =
    stateSeconds.good + stateSeconds.forwardHead + stateSeconds.slouch + stateSeconds.recline;
  const totalSec = validSec + stateSeconds.invalid;
  const validMin = validSec / 60;

  const goodRatio = validSec > 0 ? stateSeconds.good / validSec : 0;
  const fhpMin = stateSeconds.forwardHead / 60;
  const slouchMin = stateSeconds.slouch / 60;
  const reclineMin = stateSeconds.recline / 60;
  const recoveryRate =
    episodes.length === 0 ? 1 : episodes.filter((e) => e.recovered).length / episodes.length;

  const score =
    100 *
    (weights.goodRatio * goodRatio +
      weights.fhpFree * (1 - clamp01(validMin > 0 ? fhpMin / validMin : 0)) +
      weights.slouchFree * (1 - clamp01(validMin > 0 ? slouchMin / validMin : 0)) +
      weights.recovery * recoveryRate);

  const decay = decayGood.map((good, i) => {
    const valid = decayValid[i] ?? 0;
    return valid > 0 ? round2(good / valid) : 0;
  });

  return {
    durationSec: round2(totalSec),
    validRatio: totalSec > 0 ? round2(validSec / totalSec) : 0,
    score: validSec > 0 ? Math.round(score) : 0,
    goodRatio: round2(goodRatio),
    fhpMin: round2(fhpMin),
    slouchMin: round2(slouchMin),
    reclineMin: round2(reclineMin),
    recoveryRate: round2(recoveryRate),
    cva: metricStats(cvaValues),
    cha: metricStats(chaValues),
    trunk: metricStats(trunkValues),
    prot: metricStats(protValues),
    hip: metricStats(hipValues),
    episodes: episodes
      .slice()
      .sort((a, b) => a.start - b.start)
      .slice(0, TIMING.maxEpisodesStored),
    decay,
    timeToFirst: episodes.length > 0 ? episodes[0]!.start : null,
    stateSeconds,
  };
}

/** The three longest episodes, for the report's "TOP 3" block (PRODUCT §4). */
export function topEpisodes(episodes: readonly Episode[], n = 3): Episode[] {
  return [...episodes].sort((a, b) => b.end - b.start - (a.end - a.start)).slice(0, n);
}

/**
 * Which metric let the user down most this session. Drives the coaching focus
 * and the report headline. Compares each metric to its own scale, so a 30°
 * trunk lean and a 0.3 protraction are ranked fairly.
 */
export function worstMetric(aggregate: SessionAggregate): SessionSummaryDTO['worstMetric'] {
  const candidates: Array<{ key: SessionSummaryDTO['worstMetric']; severity: number }> = [];
  if (aggregate.cva) {
    // How far the typical-worst decile sits past the cut-off, in MDC bands.
    candidates.push({ key: 'cva', severity: (THRESHOLDS.cva.good - aggregate.cva.p10) / MDC_DEGREES });
  }
  if (aggregate.cha) {
    candidates.push({ key: 'cha', severity: (aggregate.cha.p90 - THRESHOLDS.cha.max) / MDC_DEGREES });
  }
  if (aggregate.trunk) {
    candidates.push({ key: 'trunk', severity: (aggregate.trunk.p90 - 10) / 10 });
  }
  if (aggregate.prot) {
    candidates.push({ key: 'prot', severity: (aggregate.prot.p90 - 0.1) / 0.1 });
  }
  const worst = candidates.sort((a, b) => b.severity - a.severity)[0];
  if (!worst || worst.severity <= 0) return 'none';
  return worst.key;
}

/** Trim a session down to the numbers the LLM is allowed to see (ARCHITECTURE §2.5). */
export function toCoachingDto(aggregate: SessionAggregate): SessionSummaryDTO {
  return {
    durationMin: round2(aggregate.durationSec / 60),
    score: aggregate.score,
    goodRatio: aggregate.goodRatio,
    fhpMin: aggregate.fhpMin,
    slouchMin: aggregate.slouchMin,
    reclineMin: aggregate.reclineMin,
    validRatio: aggregate.validRatio,
    cvaMean: aggregate.cva?.mean ?? null,
    cvaP10: aggregate.cva?.p10 ?? null,
    chaMean: aggregate.cha?.mean ?? null,
    trunkMean: aggregate.trunk?.mean ?? null,
    protMean: aggregate.prot?.mean ?? null,
    hipMean: aggregate.hip?.mean ?? null,
    episodeCount: aggregate.episodes.length,
    timeToFirstMin: aggregate.timeToFirst === null ? null : round2(aggregate.timeToFirst / 60),
    decay: aggregate.decay.slice(0, 24),
    worstMetric: worstMetric(aggregate),
  };
}
